// src/services/apiClient.ts
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  User,
  Season,
  Gara,
  PaginatedResponse,
  Statistiche,
} from '@/types';


type AxiosCfg = AxiosRequestConfig & {
  skipGlobal401Handler?: boolean;
  skipAutoParams?: boolean;
  allowWithoutToken?: boolean;
  skipUsageTracking?: boolean;
};



// === Helpers anti-loop ===
const isAuthEndpoint = (raw?: string) => {
  if (!raw) return false;
  const url = raw.split('?')[0];              // senza query
  const u = url.startsWith('/') ? url : '/' + url; // normalizza anche "auth/..."
  // Riconosci login/refresh/logout/me/blacklist ovunque nel path (/api/auth/...)
  return /\/auth\/(login|refresh|logout|me|token\/blacklist)/i.test(u);
};


const isAuthLoginEndpoint = (raw?: string) => {
  if (!raw) return false;
  const url = raw.split('?')[0];
  const u = url.startsWith('/') ? url : '/' + url;
  return /\/auth\/login\/?$/i.test(u);
};




const isOnLoginPage = () =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/login');

let __isLoggingOut = false;

const shouldSkipPortalUsageEndpoint = (raw?: string) => {
  if (!raw) return true;
  const url = raw.split('?')[0].replace(/^\/+/, '');
  if (!url || url.includes('portal-usage')) return true;
  return [
    /^gare\/dettaglio-by-id\/[^/]+\/?$/i,
    /^gare\/dettaglio\/[^/]+\/?$/i,
    /^gare\/\d+\/?$/i,
    /^designazioni\/by-gara\/[^/]+\/?$/i,
    /^stagioni\/?$/i,
    /^categorie\/?$/i,
    /^admin\/stats\/filters\/?$/i,
    /^admin\/options\/.+/i,
    /^qualifiche\/?$/i,
    /^sezioni\/?$/i,
    /^status\/?$/i,
    /^tipo-provvedimenti\/?$/i,
    /^motivazioni-provvedimento\/?$/i,
    /^tipo-persona-provvedimento\/?$/i,
    /^tipi-video-gara\/?$/i,
  ].some((pattern) => pattern.test(url));
};








type StatsFiltersResponse = {
  categorie: Array<{ id: number | string; name: string }>;
  gironi: Array<{ value: string }>;
};
// ---- Tipi anagrafiche sanzioni
export interface TipoProvvedimento { id_provvedimento: number; provvedimento_name: string; }
export interface MotivazioneProvvedimento {
  id_motivazione_provvedimento: number;
  motivazione_provvedimento_name: string;
  id_tipo_provvedimento?: number | null;
}
export interface TipoPersonaProvvedimento { id_persona_provvedimento: number; persona_provvedimento_name: string; }

export interface TipoVideoGara {
  id_tipo_video: number;
  tipo_video_name: string;
}

export interface VideoGara {
  id_video_gara: number;
  id_gara: number;
  id_tipo_video: number;
  tipo_video_nome: string;
  descrizione_video: string;
  link_video: string;
  created_by?: number | null;
  created_by_nome?: string | null;
  created_at?: string | null;
}

// ---- Tipi DRF (interni)
export type DRFGaraListItem = {
  id_gara: number;
  id_gara_s4y: number;
  id_categoria_gara?: number;
  id_stagione?: number;
  date: string;     // ISO date
  time: string;     // HH:MM:SS
};

export type DRFGaraDettaglio = {
  gara: {
    id_gara: number;
    id_gara_s4y: number;
    no_giornata: number;
    girone: string;
    andata_ritorno: string;
    date: string;
    time: string; // HH:MM
    timestamp_inserimento?: string | null;
  };
  categoria: { id_categoria_gara: number; name: string; weight: number; id_campionato?: number; } | null;
  stagione: { id_stagione: number; stagione_name: string; current: boolean } | null;
  squadra_casa: { id_squadra: number | null; id_squadra_s4y: number; name: string };
  squadra_ospite: { id_squadra: number | null; id_squadra_s4y: number; name: string };
  campo: { id_campo: number | null; id_campo_s4y: number; campo_name: string; campo_address: string | null };
  designazioni: Array<{
    id_designazione: number;
    ruolo: { id_ruolo_gara: number | null; ruolo_gara_name: string | null };
    persona: {
      id_user: number;
      nome: string;
      cognome: string;
      nome_completo: string;
      meccanografico?: string;
      cellulare?: string | null;
      email?: string | null;
    };
  }>;
};

// ---- Tipi DRF stagioni (paginato)
type Paginated<T> = { count: number; next: string | null; previous: string | null; results: T[]; };
type DRFSeason = { id_stagione: number; stagione_name: string; current: boolean };

// ---- Tipi Designazioni (minimi)
export type DRFDesignazione = {
  id_designazione: number;
  id_gara: number | { id_gara: number; id_gara_s4y?: number };
  id_ruolo_gara?: { id_ruolo_gara: number; ruolo_gara_name: string } | number;
  id_user?: { id_user: number; meccanografico?: string; nome?: string; cognome?: string } | number;
};

export type TeamComparisonStats = {
  id_squadra_s4y: number;
  nome: string;
  gare_stagionali: number;
  gare_con_rapportino: number;
  vittorie: number;
  pareggi: number;
  sconfitte: number;
  gol_fatti: number;
  gol_subiti: number;
  differenza_reti: number;
  porte_inviolate: number;
  gare_senza_segnare: number;
  media_gol_fatti: number;
  media_gol_subiti: number;
  media_recupero_primo_tempo: number;
  media_recupero_secondo_tempo: number;
  punti_per_gara: number;
  sanzioni: number;
  sanzioni_per_gara: number;
  split: {
    casa: TeamSplitStats;
    trasferta: TeamSplitStats;
  };
  forma_recente: Array<{
    data: string | null;
    contesto: 'casa' | 'trasferta';
    avversario: string;
    gol_fatti: number;
    gol_subiti: number;
    esito: 'V' | 'P' | 'S';
  }>;
  sanzioni_per_tipo: StatBreakdown[];
  sanzioni_per_motivazione: StatBreakdown[];
  sanzioni_per_persona: StatBreakdown[];
  sanzioni_per_tempo: StatBreakdown[];
  sanzioni_per_minuto: StatBreakdown[];
  sanzioni_minuto_per_tempo: Array<{
    minuto: number;
    primo_tempo: number;
    secondo_tempo: number;
  }>;
  sanzioni_tipo_per_tempo: Array<{
    nome: string;
    primo_tempo: number;
    secondo_tempo: number;
    non_disponibile: number;
  }>;
  andamento_mensile: Array<{
    mese: number;
    gare: number;
    sanzioni: number;
  }>;
};

export type TeamSplitStats = {
  gare: number;
  vittorie: number;
  pareggi: number;
  sconfitte: number;
  gol_fatti: number;
  gol_subiti: number;
};

export type StatBreakdown = { nome: string; count: number };

export type TeamPreviousSeasonHistory = {
  stagione: { id: number; nome: string } | null;
  stessa_categoria: {
    categoria: { id: number; nome: string };
    medie_categoria: GaraComparisonResponse['medie_categoria'];
    stats: TeamComparisonStats;
    lettura: string;
    terne: GaraComparisonResponse['terne'];
  } | null;
  categorie_differenti: Array<{
    categoria: { id: number; nome: string };
    medie_categoria: GaraComparisonResponse['medie_categoria'];
    stats: TeamComparisonStats;
    lettura: string;
    terne: GaraComparisonResponse['terne'];
    richiede_conferma: boolean;
  }>;
};

export type GaraComparisonResponse = {
  stagione_id: number;
  categoria: { id: number; nome: string };
  medie_categoria: {
    campione_gare: number;
    gol_per_squadra: number;
    sanzioni_per_squadra: number;
    punti_per_squadra: number;
  };
  squadra_casa: TeamComparisonStats;
  squadra_ospite: TeamComparisonStats;
  storico_precedente?: {
    squadra_casa: TeamPreviousSeasonHistory;
    squadra_ospite: TeamPreviousSeasonHistory;
  };
  precedenti: Array<{
    id_gara: number;
    data: string | null;
    squadra_casa: string;
    squadra_ospite: string;
    reti_casa: number;
    reti_ospite: number;
  }>;
  lettura_pre_gara: {
    metodo: string;
    squadra_casa: string;
    squadra_ospite: string;
    confronto: string;
  };
  terne: Array<{
    id_gara: number;
    id_squadra_casa_s4y: number;
    id_squadra_ospite_s4y: number;
    data: string | null;
    ora: string | null;
    squadra_casa: string;
    squadra_ospite: string;
    video_count?: number;
    componenti: Array<{
      ruolo: 'AE' | 'AA1' | 'AA2' | 'CR' | string;
      id_user: number;
      nome: string;
      sezione: string | null;
      qualifica: string | null;
      cellulare: string | null;
      email: string | null;
    }>;
  }>;
};


// ---- Helpers
function parseSeasonYears(label: string) {
  const m = /^(\d{4})-(\d{4})$/.exec(label);
  return { start: m ? parseInt(m[1], 10) : 0, end: m ? parseInt(m[2], 10) : 0 };
}
function hhmm(t?: string | null) { return t ? t.slice(0, 5) : ''; }

function mapToFrontGara(item: DRFGaraListItem, dett?: DRFGaraDettaglio): Gara {
  const byRole = new Map<string, string>();
  dett?.designazioni?.forEach((d) => {
    const role = (d.ruolo?.ruolo_gara_name || '').toLowerCase();
    const name = d.persona?.nome_completo || `${d.persona?.nome} ${d.persona?.cognome}`.trim();
    if (role) byRole.set(role, name);
  });
  const arbitro_principale = byRole.get('arbitro') || byRole.get('arbitro principale') || undefined;
  const arbitro_assistente1 = byRole.get('assistente i') || byRole.get('assistente 1') || undefined;
  const arbitro_assistente2 = byRole.get('assistente ii') || byRole.get('assistente 2') || undefined;
  const quarto_ufficiale = byRole.get('4° ufficiale') || byRole.get('quarto ufficiale') || undefined;

  return {
    id: String(item.id_gara),
    data_gara: item.date,
    ora_gara: hhmm(item.time),
    categoria: dett?.categoria?.name || '',
    squadra_casa: dett?.squadra_casa?.name || `Casa #${item.id_gara_s4y}`,
    squadra_ospite: dett?.squadra_ospite?.name || 'Ospite',
    campo: dett?.campo?.campo_name || '',
    stato: 'programmata',
    arbitro_principale,
    arbitro_assistente1,
    arbitro_assistente2,
    quarto_ufficiale,
    season_id: dett?.stagione?.id_stagione ? String(dett?.stagione?.id_stagione) : '',
    created_at: dett?.gara?.timestamp_inserimento || '',
    updated_at: dett?.gara?.timestamp_inserimento || '',
  };
}

class ApiClient {
  private instance: AxiosInstance;
  private baseURL: string;

  private seasonListeners = new Set<() => void>();
  private usageDedup = new Map<string, number>();
    // --- Stato per gestire un solo refresh alla volta + coda richieste ---
private __isRefreshing = false;
// Evita la forma Array<...> per non confondere il parser TS
private __failedQueue: { resolve: (token: string) => void; reject: (err: any) => void }[] = [];

private __processQueue(error: any, token: string | null = null): void {
  this.__failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(token as string)));
  this.__failedQueue = [];
}

public get<T = any>(url: string, config?: import('axios').AxiosRequestConfig) {
  return this.instance.get<T>(url, config);
}

public onSeasonChange(cb: () => void) {
  this.seasonListeners.add(cb);
  // ritorna l'unsubscribe
  return () => this.seasonListeners.delete(cb);
}

private emitSeasonChange() {
  for (const fn of this.seasonListeners) {
    try { fn(); } catch {}
  }
}

private normalizeParamsForUsage(params: any): Record<string, any> {
  if (!params) return {};
  if (params instanceof URLSearchParams) return Object.fromEntries(params.entries());
  if (typeof params !== 'object') return {};
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => [key, Array.isArray(value) ? value.map(String) : String(value)])
  );
}

private trackPortalUsage(config: AxiosRequestConfig) {
  try {
    const url = String(config.url || '').split('?')[0];
    if (!url || isAuthEndpoint(url) || shouldSkipPortalUsageEndpoint(url) || (config as AxiosCfg).skipUsageTracking) return;
    const token = localStorage.getItem('arma4you.token');
    if (!token) return;
    const method = String(config.method || 'GET').toUpperCase();
    const queryParams = this.normalizeParamsForUsage(config.params);
    const pagePath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '';
    const dedupKey = JSON.stringify({ method, url, pagePath, queryParams });
    const now = Date.now();
    const lastSeen = this.usageDedup.get(dedupKey);
    if (lastSeen && now - lastSeen < 30_000) return;
    this.usageDedup.set(dedupKey, now);
    if (this.usageDedup.size > 150) {
      const oldestKeys = Array.from(this.usageDedup.entries())
        .sort((a, b) => a[1] - b[1])
        .slice(0, 50)
        .map(([key]) => key);
      oldestKeys.forEach((key) => this.usageDedup.delete(key));
    }
    axios.post(
      `${this.baseURL}portal-usage/log/`,
      {
        method,
        path: url,
        page_path: pagePath,
        query_params: queryParams,
      },
      {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 5000,
      }
    ).catch(() => {});
  } catch {}
}

  constructor() {
    const rawBaseURL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api/';
    this.baseURL = rawBaseURL.endsWith('/') ? rawBaseURL : `${rawBaseURL}/`;
    this.instance = axios.create({ baseURL: this.baseURL, timeout: 30000, headers: { 'Content-Type': 'application/json' } });    
    this.setupInterceptors();
  }







  

  
  private getActiveSeasonFromLS(): string | null {
  try {
    const raw = localStorage.getItem('arma4you.season');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // supporta sia {id: "..."} che stringa semplice
    if (typeof parsed === 'string') return parsed;
    if (parsed?.id) return String(parsed.id);
    if (parsed?.season_id) return String(parsed.season_id);
    return null;
  } catch {
    return null;
  }
}

private getActiveCampionatoFromLS(): number | null {
  const raw = localStorage.getItem('arma4you.campionato_id');
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

public setActiveSeason(seasonId: string | number) {
  try {
    localStorage.setItem('arma4you.season', JSON.stringify({ id: String(seasonId) }));
  } finally {
    this.emitSeasonChange(); // ⬅️ avvisa tutta l’app che la season è cambiata
  }
}


/** Facoltativo: se manca in LS, prova a recuperare la season corrente dal backend */
public async ensureActiveSeason() {
  if (this.getActiveSeasonFromLS()) return;
  const res = await this.getSeasons();
  const current = res.data?.find(s => s.is_current) ?? res.data?.[0];
  if (current) this.setActiveSeason(current.id);
}





private setupInterceptors() {
  // === REQUEST ===
this.instance.interceptors.request.use(
  (config) => {
    const url = (config.url || '').split('?')[0];
    const isAuth = isAuthEndpoint(url);

    // 1) Bearer token
    const token = localStorage.getItem('arma4you.token');
    if (token) {
      config.headers = config.headers ?? {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    } else {  // ——— Manca il token ———
  // Se siamo sulla pagina di login: CONSENTI solo /auth/login (blocca TUTTO il resto, compreso /auth/me)
  if (isOnLoginPage()) {
    const isLoginCall = isAuthLoginEndpoint(url);
    if (!isLoginCall && !(config as any).allowWithoutToken) {
      // blocco secco, nessun redirect (sei già su /login)
      return Promise.reject({ __unauthenticated_on_login: true });
    }
    // se è /auth/login, prosegui normalmente
  } else {
    // Non siamo su /login: per endpoint NON-auth → redirect e blocco
    if (!isAuth && !(config as any).allowWithoutToken) {
      try {
        localStorage.removeItem('arma4you.token');
        localStorage.removeItem('arma4you.refreshToken');
      } catch {}
      if (typeof window !== 'undefined' && !__isLoggingOut) {
        __isLoggingOut = true;
        try {
          const current = window.location.pathname + window.location.search;
          window.location.replace(`/login?redirectTo=${encodeURIComponent(current)}`);
        } finally {
          __isLoggingOut = false;
        }
      }
      return Promise.reject({ __unauthenticated: true });
    }
    // se è endpoint auth/* fuori dalla /login (es. refresh o login), lascialo passare
  }
    }

    // 3) Auto-params SOLO se NON è endpoint auth e se non chiesto di saltarli
    const skipAutoParams = isAuth || (config as any).skipAutoParams;
    if (!skipAutoParams) {
      let params: Record<string, any> = {};
const incoming = config.params as any;

if (incoming instanceof URLSearchParams) {
  params = Object.fromEntries(incoming.entries());
} else if (incoming && typeof incoming === 'object') {
  params = { ...incoming };
}



      const season = this.getActiveSeasonFromLS?.();
      if (season != null && typeof params.season_id === 'undefined') {
        params.season_id = season;
        if (typeof params.id_stagione === 'undefined') params.id_stagione = season;
      }
      const campionato = this.getActiveCampionatoFromLS?.();
      if (campionato != null && typeof params.campionato_id === 'undefined') {
        params.campionato_id = campionato;
        if (typeof params.id_campionato === 'undefined') params.id_campionato = campionato;
      }
      config.params = params;
    }

    return config; // <-- IMPORTANTISSIMO
  },
  (error) => Promise.reject(error)
);



// === RESPONSE ===
this.instance.interceptors.response.use(
  (response) => {
    this.trackPortalUsage(response.config);
    return response;
  },
  async (error: any) => {
    const req = error?.config || {};
    const status = error?.response?.status;
    const url = (req.url || '').split('?')[0];

    // Non interferire con: pagina login, endpoint auth, richieste che chiedono di saltare l’handler
    if (status !== 401 || isOnLoginPage() || isAuthEndpoint(url) || (req as any).skipGlobal401Handler) {
      return Promise.reject(error);
    }

    // Se siamo già in logout, non fare altro
    if (__isLoggingOut) {
      return Promise.reject(error);
    }

    // Se un refresh è già in corso → metti la richiesta in coda e aspetta
if (this.__isRefreshing) {
  return new Promise<string>((resolve, reject) => {
    this.__failedQueue.push({ resolve, reject });
  })
    .then((newAccess) => {
      req.headers = req.headers ?? {};
      req.headers['Authorization'] = `Bearer ${newAccess}`;
      return this.instance(req);
    })
    .catch((err) => Promise.reject(err));
}


    // Primo 401: prova il refresh
    (req as any)._retry = true;
this.__isRefreshing = true;

    try {
      const refreshToken = localStorage.getItem('arma4you.refreshToken');
      if (!refreshToken) throw new Error('Missing refresh token');

      // Chiamata di refresh (NON deve triggerare questo handler)
      const { data } = await this.instance.post(
        '/auth/refresh/',
        { refresh: refreshToken },
        { skipGlobal401Handler: true, skipAutoParams: true, allowWithoutToken: true } as AxiosCfg
      );

      const newAccess = data?.access;
      if (!newAccess) throw new Error('No access token from refresh');

      // Salva e aggiorna default header
      localStorage.setItem('arma4you.token', newAccess);
      this.instance.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;

      // Sblocca la coda in successo
this.__processQueue(null, newAccess);

      // Riprova la richiesta originale
      req.headers = req.headers ?? {};
      req.headers['Authorization'] = `Bearer ${newAccess}`;
      return this.instance(req);
    } catch (refreshErr) {
      // Sblocca la coda in errore
this.__processQueue(refreshErr, null);

      // FALLBACK: logout globale + redirect a /login
      try {
        // evita race condition di doppi logout
        if (!__isLoggingOut) {
          __isLoggingOut = true;
          await this.logout(); // ripulisce storage e fa redirect se non sei già su /login
        }
      } finally {
        __isLoggingOut = false;
      }
      return Promise.reject(refreshErr);
    } finally {
this.__isRefreshing = false;
    }
  }
);




}



  private async request<T>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const resp = await this.instance.request<T>(config);
      return { data: resp.data as T, status: resp.status };
    } catch (error: any) {
      return {
        error: {
          message: error.response?.data?.message || error.response?.data?.error || error.message || 'Errore sconosciuto',
          code: error.response?.data?.code,
          details: error.response?.data?.details,
        },
        status: error.response?.status || 0,
      };
    }
  }

  // -------- Auth
  async login(body: LoginRequest) {
    return this.request<LoginResponse>({ method: 'POST', url: 'auth/login/', data: body });
  }
  async getCurrentUser() {
    return this.request<User>({ method: 'GET', url: 'auth/me/' });
  }

   async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string }>({
      method: 'POST',
      url: 'auth/change-password/',
      data: {
        current_password: currentPassword,
        new_password: newPassword,
      },
    });
  }


public async logout(): Promise<void> {
  try {
    const refresh = localStorage.getItem('arma4you.refreshToken');
    if (refresh) {
      try {
        // SimpleJWT blacklist (se presente)
        await this.instance.post(
          '/auth/token/blacklist/',
          { refresh },
  { skipGlobal401Handler: true, skipAutoParams: true, allowWithoutToken: true } as AxiosCfg
        );
      } catch {
        // Logout custom (se esiste)
        try {
          await this.instance.post(
            '/auth/logout/',
            null,
  { skipGlobal401Handler: true, skipAutoParams: true, allowWithoutToken: true } as AxiosCfg
          );
        } catch {}
      }
    } else {
      // Soft logout se non hai refresh
      try {
        await this.instance.post(
          '/auth/logout/',
          null,
  { skipGlobal401Handler: true, skipAutoParams: true, allowWithoutToken: true } as AxiosCfg
        );
      } catch {}
    }
  } finally {
    // pulizia locale + redirect pulito
    localStorage.removeItem('arma4you.token');
    localStorage.removeItem('arma4you.refreshToken');
    const alreadyOnLogin =
      typeof window !== 'undefined' && window.location.pathname.startsWith('/login');
    if (!alreadyOnLogin) {
      const current = window.location.pathname + window.location.search;
      window.location.replace(`/login?redirectTo=${encodeURIComponent(current)}`);
    }
  }
}


  // -------- Stagioni (paginato -> array)
  async getSeasons(): Promise<ApiResponse<Season[]>> {
    const res = await this.request<Paginated<DRFSeason>>({ method: 'GET', url: 'stagioni/' });
    if (res.error || !res.data) return { error: res.error, status: res.status };
    const mapped: Season[] = (res.data.results || []).map((s) => {
      const { start, end } = parseSeasonYears(s.stagione_name || '');
      return {
        id: String(s.id_stagione),
        label: s.stagione_name,
        is_current: !!s.current,
        anno_inizio: start,
        anno_fine: end,
        created_at: '',
        updated_at: '',
      };
    });
    return { data: mapped, status: 200 };
  }

  async importUsers(file: File, opts?: { dryRun?: boolean }) {
  const form = new FormData();
  form.append('file', file);
  return this.request<any>({
    method: 'POST',
    url: 'admin/import-users-xls/', // <-- assicurati che sia l'URL del tuo backend
    params: opts?.dryRun ? { dry_run: 'true' } : undefined,
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

    async getCurrentSeason() {
    const res = await this.getSeasons(); // <-- già mappa in Season[]
    if (res.error || !res.data || res.data.length === 0) {
      return { error: res.error ?? { message: 'Nessuna stagione disponibile' }, status: res.status || 400 };
    }

    const list = res.data;
    const fromLS = this.getActiveSeasonFromLS();
    if (fromLS) {
      const found = list.find(s => String(s.id) === String(fromLS));
      if (found) return { data: found, status: 200 };
    }

    const current = list.find(s => s.is_current) ?? list[0];
    return { data: current, status: 200 };
  }
  // -------- Gare (lista/ricerca)
  async getGareRaw(filters?: Record<string, any>) {
    const params = new URLSearchParams();
    if (filters) Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
    });
    return this.request<PaginatedResponse<DRFGaraListItem>>({ method: 'GET', url: 'gare/', params });
  }

  async getGareUpcoming() {
    return this.request<DRFGaraListItem[]>({ method: 'GET', url: 'gare/upcoming/' });
  }

  // ---- Dettagli gara (USARE SEMPRE id_gara_s4y)
  async getGaraDettaglioRawByS4Y(id_gara_s4y: string | number, params?: Record<string, any>) {
    // http://localhost:8000/api/gare/dettaglio/{id_gara_s4y}/
    return this.request<DRFGaraDettaglio>({ method: 'GET', url: `gare/dettaglio/${id_gara_s4y}/`, params });
  }

  // Alias coerente con il resto del frontend
  async getGaraDettaglioRaw(id_gara_s4y: string | number) {
    return this.getGaraDettaglioRawByS4Y(id_gara_s4y);
  }

  // 🔥 NUOVO METODO: Dettaglio gara by ID interno
  async getGaraDettaglioRawById(id_gara: string | number, params?: Record<string, any>) {
    return this.request<DRFGaraDettaglio>({ method: 'GET', url: `gare/dettaglio-by-id/${id_gara}/`, params });
  }

  // Quando abbiamo SOLO l'id interno, ricavo s4y da /gare/{id_gara}/ e poi chiamo il dettaglio S4Y
  async getGaraBaseById(id_gara: string | number, params?: Record<string, any>) {
    return this.request<any>({ method: 'GET', url: `gare/${id_gara}/`, params });
  }

  async getGaraDettaglioByInternalId(id_gara: string | number, params?: Record<string, any>) {
    // 🔥 Prima prova con il nuovo endpoint diretto
    const direct = await this.getGaraDettaglioRawById(id_gara, params);
    if (direct.data) return direct;
    
    // Fallback: metodo vecchio
    const base = await this.getGaraBaseById(id_gara, params);
    if (base.error || !base.data) return { error: base.error, status: base.status } as ApiResponse<DRFGaraDettaglio>;
    const id_s4y = (base.data as any).id_gara_s4y;
    if (!id_s4y) return { error: { message: 'id_gara_s4y non disponibile' }, status: 400 } as ApiResponse<DRFGaraDettaglio>;
    return this.getGaraDettaglioRawByS4Y(id_s4y, params);
  }

  // Compat: alcune pagine chiamano getGara(id) aspettandosi il dettaglio
  async getGara(id_gara_or_s4y: string | number) {
    // Provo prima come s4y, se fallisce cerco come id interno
    const asS4Y = await this.getGaraDettaglioRawByS4Y(id_gara_or_s4y);
    if (asS4Y.data) return asS4Y;
    return this.getGaraDettaglioByInternalId(id_gara_or_s4y);
  }

  // ---- Mappatura per tabella front (usa SOLO S4Y)
  private async getGaraDettaglioSmart(item: DRFGaraListItem): Promise<DRFGaraDettaglio | null> {
    try {
      const r1 = await this.getGaraDettaglioRawByS4Y(item.id_gara_s4y);
      if (r1.data) return r1.data;
    } catch {}
    return null;
  }

  async getGareMapped(filters?: Record<string, any>) {
    const res = await this.getGareRaw(filters);
    if (res.error || !res.data) return { error: res.error, status: res.status } as ApiResponse<PaginatedResponse<Gara>>;
    const items = res.data.results;

    const detts: Array<DRFGaraDettaglio | null> = await Promise.all(items.map((it) => this.getGaraDettaglioSmart(it)));
    const mapped: Gara[] = items.map((it, idx) => mapToFrontGara(it, detts[idx] || undefined));
    return { data: { count: res.data.count, next: null, previous: null, results: mapped }, status: 200 };
  }

  async getGare(filters?: Record<string, any>): Promise<ApiResponse<PaginatedResponse<Gara>>> {
    return this.getGareMapped(filters);
  }

  // -------- Ricerca gare (fallback client-side)
  async searchGare(query: string) {
    const res = await this.getGareMapped({ page_size: 50 });
    if (res.error || !res.data) return { error: res.error, status: res.status } as ApiResponse<Gara[]>;
    const q = query.toLowerCase();
    const filtered = res.data.results.filter(
      (g) =>
        g.squadra_casa.toLowerCase().includes(q) ||
        g.squadra_ospite.toLowerCase().includes(q) ||
        g.categoria.toLowerCase().includes(q)
    );
    return { data: filtered.slice(0, 20), status: 200 } as ApiResponse<Gara[]>;
  }

  // -------- Designazioni
  async getMyDesignazioni(params?: Record<string, any>) {
    return this.request<DRFDesignazione[]>({ method: 'GET', url: 'designazioni/my_designations/', params });
  }
  async getDesignazioniByGaraS4Y(id_gara_s4y: string | number) {
    return this.request<DRFDesignazione[]>({ method: 'GET', url: `designazioni/by-gara/${id_gara_s4y}/` });
  }
  async getGaraComparison(id_gara: string | number, season_id?: string | number) {
    return this.request<GaraComparisonResponse>({
      method: 'GET',
      url: `gare/${id_gara}/confronto-squadre/`,
      params: season_id ? { season_id } : undefined,
    });
  }
  async getTipiVideoGara() {
    return this.request<TipoVideoGara[]>({ method: 'GET', url: 'tipi-video-gara/' });
  }

  async getVideoGara(id_gara: string | number) {
    return this.request<VideoGara[]>({
      method: 'GET',
      url: 'video-gara/',
      params: { id_gara },
    });
  }

  async createVideoGara(data: {
    id_gara: number;
    id_tipo_video: number;
    descrizione_video: string;
    link_video: string;
  }) {
    return this.request<VideoGara>({ method: 'POST', url: 'video-gara/', data });
  }

  async deleteVideoGara(id_video_gara: string | number) {
    return this.request<void>({ method: 'DELETE', url: `video-gara/${id_video_gara}/` });
  }
async getRuoliGara(params?: { allowed?: number[] }) {
  const query = params?.allowed?.map(id => `allowed=${id}`).join("&") ?? "";
  const res = await this.get(`/ruoli-gara/${query ? "?" + query : ""}`);
  // Forziamo sempre un array
  return Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
}


  // -------- Rapportini
  async createRapportinoWithSanzioni(data: {
    id_gara: number;
    reti_squadra_casa: number;
    reti_squadra_ospite: number;
    provvedimenti: Array<{
      id_tipo_provvedimento: number;
      id_motivazione_provvedimento: number;
      id_persona_provvedimento: number;
      id_squadra_s4y: number;
      minuto?: number | null;
    }>;
  }) {
    return this.request<any>({ method: 'POST', url: 'rapportini/', data });
  }

  async createRapportino(data: any) {
    return this.request<any>({ method: 'POST', url: 'rapportini/', data });
  }

  async updateRapportino(id_rapportino: string | number, data: any) {
    return this.request<any>({ method: 'PUT', url: `rapportini/${id_rapportino}/`, data });
  }

  async getRapportino(id_rapportino: string | number) {
    return this.request<any>({ method: 'GET', url: `rapportini/${id_rapportino}/` });
  }

  async richiediRiaperturaRapportino(id_rapportino: string | number, note?: string) {
    return this.request<any>({
      method: 'POST',
      url: `rapportini/${id_rapportino}/richiedi-riapertura/`,
      data: { note: note || '' },
    });
  }

  async getRichiesteRiaperturaRapportini(stato?: string) {
    return this.request<any>({
      method: 'GET',
      url: 'rapportini-riaperture/',
      params: stato ? { stato } : undefined,
    });
  }

  async approvaRiaperturaRapportino(id_richiesta: string | number) {
    return this.request<any>({
      method: 'POST',
      url: `rapportini-riaperture/${id_richiesta}/approva/`,
    });
  }

  async rifiutaRiaperturaRapportino(id_richiesta: string | number) {
    return this.request<any>({
      method: 'POST',
      url: `rapportini-riaperture/${id_richiesta}/rifiuta/`,
    });
  }

  async getRapportinoByGaraId(id_gara: string | number) {
    return this.request<any>({ method: 'GET', url: `rapportini/by-gara/${id_gara}/` });
  }

  async getRapportinoByGaraS4Y(id_gara_s4y: string | number) {
    return this.request<any>({ method: 'GET', url: `rapportini/by-gara-s4y/${id_gara_s4y}/` });
  }

  async getMyRapportini() {
    return this.request<any[]>({ method: 'GET', url: 'rapportini/my_rapportini/' });
  }

  // -------- Statistiche
  async getStatistiche(seasonId?: string): Promise<ApiResponse<Statistiche>> {
    let sid = seasonId;
    if (!sid) {
      try {
        const raw = localStorage.getItem('arma4you.season');
        if (raw) sid = JSON.parse(raw)?.id;
      } catch {}
    }
    const params = new URLSearchParams();
    if (sid) params.append('season_id', String(sid));
    return this.request<Statistiche>({ method: 'GET', url: 'admin/dashboard/', params });
  }





getStatsFilters(params?: { categoria_id?: string | number }) {
  // NB: baseURL già include /api/, quindi qui NON rimettere "api/"
  return this.request<StatsFiltersResponse>({
    method: 'GET',
    url: 'admin/stats/filters/',
    params,
  });
}


getUsers<T = any>(params: {
  page?: number; page_size?: number; search?: string; ordering?: string;
  id_qualifica?: number|string; id_sezione?: number|string; status?: number|string;
  role?: string; is_active?: boolean|string;
}) {
  return this.request<Paginated<T>>({
    method: 'GET',
    url: 'users/',
    params,
  });
}



  adminResetPassword(payload: { user_id: number|string; new_password: string }) {
    return this.request({
      method: 'POST',
      url: 'admin/users/reset-password/',
      data: payload,
    });
  }
  adminToggleActive(payload: { user_id: number|string; is_active: boolean }) {
    return this.request({
      method: 'POST',
      url: 'admin/users/toggle-active/',
      data: payload,
    });
  }
  adminAlignAccessToStatus() {
    return this.request<{
      ok: boolean;
      checked: number;
      updated: number;
      activated: number;
      deactivated: number;
      without_auth: number;
    }>({
      method: 'POST',
      url: 'admin/users/align-access-to-status/',
    });
  }
adminSetUserRole(payload: { user_id: number|string; role: string }) {
  return this.request({
    method: 'POST',
    url: 'admin/set-role/',
    // il backend vuole `new_role`
    data: { user_profile_id: payload.user_id, new_role: payload.role },
  });
}

  getQualifiche() { return this.request({ method: 'GET', url: 'qualifiche/', params: { page_size: 200 }}); }
  getSezioni()    { return this.request({ method: 'GET', url: 'sezioni/',   params: { page_size: 200 }}); }
  getStatus()     { return this.request({ method: 'GET', url: 'status/',    params: { page_size: 200 }}); }


 
  // -------- Anagrafiche sanzioni
  async getTipiProvvedimento() {
    return this.request<TipoProvvedimento[]>({ method: 'GET', url: 'tipo-provvedimenti/' });
  }
  async getMotivazioniProvvedimento() {
    return this.request<MotivazioneProvvedimento[]>({ method: 'GET', url: 'motivazioni-provvedimento/' });
  }
  async getTipiPersonaProvvedimento() {
    return this.request<TipoPersonaProvvedimento[]>({ method: 'GET', url: 'tipo-persona-provvedimento/' });
  }


// Alias storico: se altrove avevi getStatsGenerale, continua a funzionare
getStatsGenerale(
  params: {
    season_id?: string | number;
    periodo?: string;
    data_inizio?: string;
    data_fine?: string;
  } = {}
) {
  return this.getStatisticsOverview(params);
}



getStatisticsOverview(
  params: {
    season_id?: string | number;
    periodo?: string;
    data_inizio?: string;
    data_fine?: string;
    user_id?: string | number;          // <-- per TAB Arbitri
    id_squadra_s4y?: string | number;   // <-- per TAB Società
    id_categoria_gara?: string | number;// <-- per TAB Società
  } = {}
) {
  return this.request<any>({ method: 'GET', url: 'admin/stats/general/', params });
}


// --- FALLI PER MINUTO ---
getFalliPerMinuto(params: {
  season_id?: string | number;
  periodo?: string;
  data_inizio?: string;
  data_fine?: string;
  tipo_id?: string | number;
  motivazione_id?: string | number;
  persona_id?: string | number;
  user_id?: string | number;          // <-- per TAB Arbitri
  id_squadra_s4y?: string | number;   // <-- per TAB Società
  id_categoria_gara?: string | number;// <-- per TAB Società
} = {}) {
  return this.request<any>({
    method: 'GET',
    url: 'admin/stats/falli-per-minuto/',
    params,
  });
}


async getStatsArbitro(params: {
  user_id?: string | number;       // <-- opzionale
  season_id?: string | number;
  periodo?: string;
  data_inizio?: string;
  data_fine?: string;
}) {
  return this.request<any>({ method: 'GET', url: 'admin/stats/arbitro/', params });
}

async getStatsSocieta(params: {
  id_categoria_gara: string | number
  id_squadra_s4y: string | number
  season_id?: string | number
  periodo?: string
  data_inizio?: string
  data_fine?: string
}) {
  return this.request<any>({ method: 'GET', url: 'admin/stats/societa/', params });
}

async getArbitriOptions() {
  return this.request<Array<{ id_user: number; nome: string; cognome: string; meccanografico?: string }>>({
    method: 'GET',
    url: 'admin/options/arbitri/',
  });
}

async getSocietaByCategoria(id_categoria_gara: number | string) {
  return this.request<Array<{ id_squadra_s4y: number; name: string }>>({
    method: 'GET',
    url: 'admin/options/societa-by-categoria/',
    params: { id_categoria_gara },
  });
}
async getCategorieGara() {
  // endpoint reale: /api/categorie/
  const res = await this.request<any>({ method: 'GET', url: 'categorie/' });
  if (res.error || !res.data) return res;
  const data = Array.isArray(res.data) ? res.data : (res.data.results ?? []);
  return { data, status: res.status };
}

async getRapportiniMonitoring(params: Record<string, any>) {
  return this.request<any>({ method: 'GET', url: 'admin/rapportini-monitoring/', params });
}

async sollecitaRapportino(id_gara: number | string) {
  return this.request<any>({
    method: 'POST',
    url: 'admin/rapportini-monitoring/sollecita/',
    data: { id_gara },
  });
}

async getRapportiniEmailSettings() {
  return this.request<any>({ method: 'GET', url: 'admin/rapportini-monitoring/settings/' });
}

async updateRapportiniEmailSettings(data: any) {
  return this.request<any>({
    method: 'PUT',
    url: 'admin/rapportini-monitoring/settings/',
    data,
  });
}

async sendRapportiniWeeklyReport(data?: Record<string, any>) {
  return this.request<any>({
    method: 'POST',
    url: 'admin/rapportini-monitoring/send-weekly-report/',
    data: data || {},
  });
}

async getPortalUsageMonitoring(params: Record<string, any>) {
  return this.request<any>({ method: 'GET', url: 'admin/portal-usage/', params, skipUsageTracking: true } as AxiosCfg);
}

async getStatistichePreGaraEnabledCategories() {
  return this.request<{ enabled_category_ids: number[] }>({
    method: 'GET',
    url: 'statistiche-pre-gara/categories-enabled/',
    skipUsageTracking: true,
  } as AxiosCfg);
}

async getVideoGaraEnabledCategories() {
  return this.request<{ enabled_category_ids: number[] }>({
    method: 'GET',
    url: 'video-gara/categories-enabled/',
    skipUsageTracking: true,
  } as AxiosCfg);
}

async getStatistichePreGaraSettings() {
  return this.request<any>({
    method: 'GET',
    url: 'admin/settings/statistiche-pre-gara/',
    skipUsageTracking: true,
  } as AxiosCfg);
}

async updateStatistichePreGaraSettings(payload: {
  enabled_category_ids: Array<number | string>;
  video_enabled_category_ids: Array<number | string>;
}) {
  return this.request<any>({
    method: 'PUT',
    url: 'admin/settings/statistiche-pre-gara/',
    data: payload,
    skipUsageTracking: true,
  } as AxiosCfg);
}

async logPortalUsage(data: Record<string, any>) {
  return this.request<any>({
    method: 'POST',
    url: 'portal-usage/log/',
    data,
    skipUsageTracking: true,
  } as AxiosCfg);
}


getStatsRecupero(params: {
  season_id?: string | number;
  periodo?: string;
  data_inizio?: string;
  data_fine?: string;
  id_categoria_gara?: string | number;
  girone?: string;
  user_id?: string | number;          // <-- opzionale
  id_squadra_s4y?: string | number;   // <-- opzionale
}) {
  return this.instance.get("admin/stats/recupero/", { params });
}


async importGareCSV(
  file: File,
  opts?: {
    dryRun?: boolean;
    applyChanges?: Array<{
      id_gara_s4y: number;
      ruolo: string;
      to_user_meccanografico?: string | null;
      to_user_id?: number | null;
    }>;
  }
) {
  const form = new FormData();
  form.append('file', file);
  if (opts?.applyChanges?.length) {
    form.append('apply_changes', JSON.stringify(opts.applyChanges));
  }

  return this.request<any>({
    method: 'POST',
    url: 'admin/import-gare-csv/',              // baseURL = http://127.0.0.1:8000/api/
    params: opts?.dryRun ? { dry_run: 'true' } : undefined,
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' }, // sovrascrive il default JSON
    timeout: 120000,
  });
}


}

export interface CategoriaGara {
  id_categoria_gara: number;
  name: string;
  weight: number;
}

export type Rapportino = {
  id_rapportino: number
  id_gara: number
  id_inseritore: number
  reti_squadra_casa: number
  reti_squadra_ospite: number
  recupero_primo_tempo: number
  recupero_secondo_tempo: number
  reti_rigori_realizzate_squadra_casa?: number;
  reti_rigori_realizzate_squadra_ospite?: number;
  reti_rigori_non_realizzate_squadra_casa?: number;
  reti_rigori_non_realizzate_squadra_ospite?: number;
  tiri_liberi_realizzati_squadra_casa?: number | null
  tiri_liberi_realizzati_squadra_ospite?: number | null
  tiri_liberi_non_realizzati_squadra_casa?: number | null
  tiri_liberi_non_realizzati_squadra_ospite?: number | null
  modifica_sbloccata?: boolean
  can_edit?: boolean
  can_request_reopen?: boolean
  reopen_request_status?: 'pending' | 'approved' | 'rejected' | null
  
  timettamp_creazione_rapportino: string
gara_info: {
  id_gara_s4y: number;
  data?: string;
  ora?: string;
  squadra_casa?: string;
  squadra_ospite?: string;
  categoria?: string | {
    id_categoria_gara: number;
    name: string;
    nome_completo?: string | null;
    id_campionato?: number | null;
    weight?: number | null;
  };
},
  inseritore_info?: { id_user: number; nome_completo: string; meccanografico?: string | null }
  provvedimenti: Array<{
    id_provvedimento_disciplinare: number
    squadra: { id_squadra_s4y: number; nome: string }
    tipo_provvedimento: { id: number; nome: string }
    motivazione: { id: number; nome: string }
    persona_provvedimento: { id: number; nome: string }
    tempo?: 1 | 2 | null
    tempo_label?: '1T' | '2T' | null
    minuto?: number | null
    id_ruolo_gara_assegnatario?: number | null
    ruolo_gara_nome?: string | null
  }>
}



export const apiClient = new ApiClient();
export default apiClient;

export async function fetchRapportino(id: string | number): Promise<Rapportino> {
  const res = await apiClient.getRapportino(id)
  if (res.error || !res.data) {
    throw new Error(res.error?.message ?? 'Impossibile caricare il rapportino')
  }
  return res.data as Rapportino





}

